import { AdminRole } from "@repo/api-dtos"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { X } from "lucide-react"
import * as React from "react"
import { PageHeader } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { useAdminRolesContext } from "@/contexts/admin-roles-context"
import { DebouncedSearchInput } from "@/features/admin-grid/DebouncedSearchInput"
import { ModerationAdminGrid } from "@/features/moderation-admin/ModerationAdminGrid"
import type { ModerationAdminInput } from "@/features/moderation-admin/moderation-datasource"
import { SEVERITY_LABELS } from "@/features/moderation-admin/moderation-labels"
import {
	DEFAULT_MODERATION_SORT,
	type ModerationSortDir,
	type ModerationSortField,
	normalizeModerationSearch,
	parseSeverity,
	parseSort,
	parseStatus,
	STATUS_ALL,
	serializeStatus,
	splitCsv,
	toggleCsvValue,
} from "@/features/moderation-admin/moderation-search"
import {
	QuickFilter,
	QuickFilterGroup,
} from "@/features/moderation-admin/QuickFilter"
import { useModerationCounts } from "@/queries/marketplace"
import type {
	ModerationCaseStatus,
	ModerationSeverity,
} from "@/types/marketplace.types"

export const Route = createFileRoute("/moderation/")({
	component: ModerationQueuePage,
	validateSearch: normalizeModerationSearch,
})

const SEVERITY_CHIPS: ModerationSeverity[] = [
	"critical",
	"high",
	"medium",
	"low",
]
const STATUS_CHIPS: { value: ModerationCaseStatus; label: string }[] = [
	{ value: "open", label: "Open" },
	{ value: "reviewing", label: "Reviewing" },
	{ value: "resolved", label: "Resolved" },
]

function ModerationQueuePage() {
	const searchParams = useSearch({ from: "/moderation/" })
	const navigate = useNavigate()
	const { hasRole } = useAdminRolesContext()
	const canModerate = hasRole(AdminRole.MarketplaceUserManagement)
	const counts = useModerationCounts({ enabled: canModerate })

	React.useEffect(() => {
		if (!canModerate) navigate({ to: "/unauthorized" })
	}, [canModerate, navigate])

	const pageIndex = (searchParams.page ?? 1) - 1

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/moderation",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) =>
			updateFilters({ search: value || undefined, page: undefined }),
		[updateFilters],
	)

	const togglePending = React.useCallback(
		() =>
			updateFilters({
				pending: searchParams.pending === "1" ? undefined : "1",
				page: undefined,
			}),
		[updateFilters, searchParams.pending],
	)

	const toggleSeverity = React.useCallback(
		(severity: ModerationSeverity) =>
			updateFilters({
				severity: toggleCsvValue(searchParams.severity, severity),
				page: undefined,
			}),
		[updateFilters, searchParams.severity],
	)

	const statusValues = parseStatus(searchParams.status) ?? []
	const statusAll = searchParams.status === STATUS_ALL

	const toggleStatus = React.useCallback(
		(status: ModerationCaseStatus) => {
			const current = statusAll ? [] : statusValues
			const next = current.includes(status)
				? current.filter((s) => s !== status)
				: [...current, status]
			updateFilters({ status: serializeStatus(next), page: undefined })
		},
		[updateFilters, statusAll, statusValues],
	)

	const toggleAll = React.useCallback(
		() =>
			updateFilters({
				status: statusAll ? undefined : STATUS_ALL,
				page: undefined,
			}),
		[updateFilters, statusAll],
	)

	const { sortBy, sortDir } = React.useMemo(
		() => parseSort(searchParams.sort),
		[searchParams.sort],
	)

	const input = React.useMemo<ModerationAdminInput>(
		() => ({
			search: searchParams.search,
			pendingConfirmation: searchParams.pending === "1" ? true : undefined,
			severity: parseSeverity(searchParams.severity),
			status: parseStatus(searchParams.status),
			userId: searchParams.userId,
			sortBy,
			sortDir,
		}),
		[
			searchParams.search,
			searchParams.pending,
			searchParams.severity,
			searchParams.status,
			searchParams.userId,
			sortBy,
			sortDir,
		],
	)

	const handlePageChange = React.useCallback(
		(nextIndex: number) =>
			updateFilters({ page: nextIndex === 0 ? undefined : nextIndex + 1 }),
		[updateFilters],
	)

	const handleSortChange = React.useCallback(
		(by: ModerationSortField, dir: ModerationSortDir) => {
			const sort = `${by}:${dir}`
			updateFilters({
				sort: sort === DEFAULT_MODERATION_SORT ? undefined : sort,
			})
		},
		[updateFilters],
	)

	if (!canModerate) return null

	const activeSeverities = splitCsv(searchParams.severity)
	const pendingActive = searchParams.pending === "1"

	const filters = (
		<>
			<DebouncedSearchInput
				defaultValue={searchParams.search ?? ""}
				onDebouncedChange={handleDebouncedSearch}
				placeholder="Search by username…"
			/>
			<QuickFilterGroup aria-label="Needs confirmation">
				<QuickFilter
					active={pendingActive}
					onClick={togglePending}
					count={counts.data?.pendingConfirmation}
					title="Accounts the pipeline suspended automatically. Confirm or dismiss."
				>
					Needs confirmation
				</QuickFilter>
			</QuickFilterGroup>
			<QuickFilterGroup aria-label="Severity">
				{SEVERITY_CHIPS.map((severity) => (
					<QuickFilter
						key={severity}
						active={activeSeverities.includes(severity)}
						onClick={() => toggleSeverity(severity)}
						count={counts.data?.bySeverity[severity]}
						title={`Open ${severity} cases`}
					>
						{SEVERITY_LABELS[severity]}
					</QuickFilter>
				))}
			</QuickFilterGroup>
			<QuickFilterGroup aria-label="Status">
				{STATUS_CHIPS.map((chip) => (
					<QuickFilter
						key={chip.value}
						active={!statusAll && statusValues.includes(chip.value)}
						onClick={() => toggleStatus(chip.value)}
					>
						{chip.label}
					</QuickFilter>
				))}
				<QuickFilter active={statusAll} onClick={toggleAll}>
					All
				</QuickFilter>
			</QuickFilterGroup>
			{searchParams.userId && (
				<Button
					variant="outline"
					size="sm"
					onClick={() => updateFilters({ userId: undefined, page: undefined })}
					title="Showing one account's cases. Click to clear."
				>
					User{" "}
					<span className="font-mono">{searchParams.userId.slice(-6)}</span>
					<X />
				</Button>
			)}
		</>
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-6 p-6">
			<PageHeader
				title="Moderation queue"
				count={
					counts.data ? `${counts.data.open.toLocaleString()} open` : undefined
				}
				description="One case per account with flagged messages. Automatic suspensions wait here until you confirm or dismiss them."
			/>
			<div className="min-h-0 flex-1">
				<ModerationAdminGrid
					input={input}
					pageIndex={pageIndex}
					onPageChange={handlePageChange}
					onSortChange={handleSortChange}
					filters={filters}
				/>
			</div>
		</div>
	)
}
